// src/lib/guards/requireSurface.ts
import { redirect } from '@tanstack/react-router';

import { useAuthStore } from '@/stores/authStore';
import { pickHighestAdminRole, STAFF_ROLES, type RoleType } from '@/lib/rbac';

export type AdminSurface = 'platform' | 'workspace' | 'clinical';

function destinationForNonAdmin(role: RoleType | undefined): string {
  if (!role) return '/login';
  if (role === 'client') return '/client';
  if (STAFF_ROLES.includes(role)) return '/app';
  return '/login';
}

/**
 * TanStack Router beforeLoad guard: enforce that the current user belongs
 * on this admin surface. Redirects otherwise.
 *
 *   - Platform surface: only super_admin
 *   - Workspace surface: ba_super_admin + ba_admin
 *   - Clinical surface (/app): staff roles that are not admins (psychologists,
 *     chiefs, customer_support, org_user)
 *   - Cross-surface attempts: bounced to the user's own area
 *
 * Must be called AFTER requireAuth.
 */
/* eslint-disable @typescript-eslint/only-throw-error */
export function requireSurface(surface: AdminSurface): void {
  const { user } = useAuthStore.getState();
  if (!user) {
    throw redirect({ to: '/login', search: {} as never });
  }
  const adminRole = pickHighestAdminRole(user.role);
  const userRole = user.role ?? undefined;

  if (surface === 'platform') {
    if (adminRole === 'super_admin') return;
    if (adminRole === 'ba_super_admin' || adminRole === 'ba_admin') {
      throw redirect({ to: '/admin/workspace' });
    }
    throw redirect({ to: destinationForNonAdmin(userRole) as never });
  }

  if (surface === 'workspace') {
    if (adminRole === 'ba_super_admin' || adminRole === 'ba_admin') return;
    if (adminRole === 'super_admin') {
      throw redirect({ to: '/admin/platform' });
    }
    throw redirect({ to: destinationForNonAdmin(userRole) as never });
  }

  // surface === 'clinical'
  if (adminRole === 'super_admin') {
    throw redirect({ to: '/admin/platform' });
  }
  if (adminRole === 'ba_super_admin' || adminRole === 'ba_admin') {
    throw redirect({ to: '/admin/workspace' });
  }
  if (userRole && STAFF_ROLES.includes(userRole)) return;
  throw redirect({ to: destinationForNonAdmin(userRole) as never });
}
/* eslint-enable @typescript-eslint/only-throw-error */
